import * as React from 'react';

import { Grid } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ShowFnOutput, useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';

import { venueGetSubscriptionSessionApi } from '../../../../api/stripe/venueGetSubscriptionSessionApi';
import SubscriptionDialog, {
  SubscriptionDialogProps,
} from '../../../../components/Dialogs/Subscriptions/SubscriptionDialog';
import { useAppSelector } from '../../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import { useMigrationRenewal } from '../../../../components/Dialogs/MigrationRenewal/useMigrationRenewal';
import {
  PlanPrice,
  getBrandPlanPriceByCycle,
  getBrandPlanPriceBySku,
  getPlanPriceByCycle,
  getPlanPriceBySku,
} from '../../common/helper';
import { useSubscription } from '../../context/Subscription.context';
import { Plan, SubscriptionConditionKind, brandPlans, plans } from '../../types';
import PlanContent, { PlanContentProps } from './PlanContent';

const Plans: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { tags: venueTags, type: venueType } = useAppSelector((state) => state.venue.value) || {};
  const { pricing, subscription } = useAppSelector((state) => state.subscription.value);

  const { state } = useSubscription();
  const { showModal } = useModal();
  const migrationRenewal = useMigrationRenewal();

  const modalRef = React.useRef<ShowFnOutput<SubscriptionDialogProps>>();

  const isExistingOldSubscriber = React.useMemo(() => {
    // Brands flagged for migration renewal: derive the tier from their previous
    // SKU so legacy users keep seeing legacy plans and v3 users keep seeing v3.
    if (
      migrationRenewal.needsMigrationRenewal &&
      migrationRenewal.isBrand &&
      migrationRenewal.lastSku
    ) {
      return !migrationRenewal.lastSku.replace(/_+$/, '').endsWith('_v3');
    }
    // Only old (non _v3) brand subscribers should get the old promotion limits.
    if (!subscription?.sku) return false;
    return !subscription.sku.endsWith('_v3');
  }, [
    subscription,
    migrationRenewal.needsMigrationRenewal,
    migrationRenewal.isBrand,
    migrationRenewal.lastSku,
  ]);

  const planPrices = React.useMemo<PlanPrice | null>(() => {
    if (!pricing) {
      return null;
    }

    const plans = venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
      ? getBrandPlanPriceByCycle('brand', pricing, !isExistingOldSubscriber)
      : getPlanPriceByCycle(state.billingCycle, pricing, 'venue');
    return plans;
  }, [state.billingCycle, pricing, venueTags, isExistingOldSubscriber]);

  const mutation = useMutation({
    mutationFn: venueGetSubscriptionSessionApi,
    onSuccess: (sessionUrl) => {
      window.location.href = sessionUrl;
    },
    onError: () => {
      enqueueSnackbar('Something went wrong. Please try again later', { variant: 'error' });
      window.location.reload();
    },
  });

  const handleSubscriptionClick = React.useCallback<PlanContentProps['onSubscriptionClick']>(
    (v) => {
      const isBrand = venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans);

      const skuSuffix = isBrand && !isExistingOldSubscriber ? 'v3' : '';
      const sku = isBrand
        ? `${v.value}_brand_${state.billingCycle}_${skuSuffix}`
        : `${v.value}_venue_${state.billingCycle}_`;

      // Brands need the v3-aware lookup so legacy subscribers don't accidentally
      // get matched against a v3 price (both share the `<plan>_brand_<cycle>_`
      // prefix and `getPlanPriceBySku` would pick whichever appears first).
      const price = isBrand
        ? getBrandPlanPriceBySku(sku, pricing ?? [], !isExistingOldSubscriber)
        : getPlanPriceBySku(sku, pricing ?? []);

      if (!state.currentSubscription) {
        if (price) {
          mutation.mutate(price.id);
          return true;
        }
        return;
      }

      const kind: SubscriptionConditionKind =
        v.value === 'basic' && v.logicCondition.kind === 'plan-downgrade'
          ? 'plan-cancel'
          : v.logicCondition.kind;

      modalRef.current = showModal(SubscriptionDialog, {
        price,
        plan: v.value,
        kind,
        subscription,
        modalRef,
      });
    },
    [
      showModal,
      subscription,
      state.billingCycle,
      state.currentSubscription,
      pricing,
      mutation,
      venueTags,
      isExistingOldSubscriber,
    ],
  );

  const isHighlight = React.useCallback(
    (p: Plan) => {
      if (state.currentSubscription) {
        return state.plan === p;
      }
      return p === 'starter';
    },
    [state.currentSubscription, state.plan],
  );

  // Animated glow follows the next upgrade target: free/basic → Starter, Starter → Plus, Plus → none.
  const upgradeTarget = React.useMemo<'starter' | 'plus' | undefined>(() => {
    if (!state.currentSubscription) return 'starter';
    if (state.plan === 'plus') return undefined;
    if (state.plan === 'starter') return 'plus';
    return 'starter';
  }, [state.currentSubscription, state.plan]);

  const selectedPlans = venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans) ? brandPlans : plans;

  return (
    <Grid spacing={3} container>
      {selectedPlans.map((x, i) => (
        <Grid item xs={4} key={i}>
          <PlanContent
            key={i}
            subscriptionState={state}
            value={x}
            highlight={isHighlight(x)}
            disabled={Boolean(state.currentSubscription)}
            disableButton={mutation.isPending}
            price={planPrices?.[x]}
            onSubscriptionClick={handleSubscriptionClick}
            isBrand={venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans)}
            venueType={venueType}
            isExistingOldSubscriber={isExistingOldSubscriber}
            glow={upgradeTarget === x ? upgradeTarget : undefined}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default Plans;
